import { createPortal } from 'react-dom';
import React, { useState } from 'react';
import type { Vendor, VendorDocument, VendorReview, VendorContact } from '../../services/vendorAPI';
import vendorAPI from '../../services/vendorAPI';
import {
  X,
  Edit,
  Archive,
  Shield,
  FileText,
  Clock,
  Users,
  ExternalLink,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Upload,
  Plus,
  RefreshCw,
  Calendar,
  Globe,
  Lock,
  Database,
  Settings
} from 'lucide-react';
import HelpIconButton from '../TrustPortal/HelpIconButton';
import type { TrustPortalScreenId } from '../../data/trustPortalHelpText';
import toast from 'react-hot-toast';

// Trust Portal help icons — the task spec wires the same help text for
// Railway/Anthropic/OpenAI/Mistral that appears in the Trust Portal
// section, so reviewers get context from either entry point. Vendors not
// in this map simply don't see a help icon (zero visual change for
// everyone else).
const VENDOR_HELP_SCREEN_MAP: Record<string, TrustPortalScreenId> = {
  railway: 'vendor-profile-railway',
  anthropic: 'vendor-profile-anthropic',
  openai: 'vendor-profile-openai',
  mistral: 'vendor-profile-mistral',
};

const resolveVendorHelpScreenId = (vendorName: string | undefined): TrustPortalScreenId | null => {
  if (!vendorName) return null;
  const normalized = vendorName.trim().toLowerCase();
  return VENDOR_HELP_SCREEN_MAP[normalized] ?? null;
};

interface VendorDetailProps {
  vendor: Vendor;
  onClose: () => void;
  onEdit: () => void;
  onArchive: (reason: string) => void;
  onUpdate: () => void;
}

type DetailTab = 'overview' | 'documents' | 'reviews' | 'contacts' | 'onboarding';

const VendorDetail: React.FC<VendorDetailProps> = ({
  vendor,
  onClose,
  onEdit,
  onArchive,
  onUpdate
}) => {
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCompleteReviewModal, setShowCompleteReviewModal] = useState(false);
  const [completingReviewId, setCompletingReviewId] = useState<number | null>(null);
  const [isCompletingReview, setIsCompletingReview] = useState(false);
  const [isStartingReview, setIsStartingReview] = useState(false);
  const [completeReviewForm, setCompleteReviewForm] = useState({
    recommendation: 'continue' as 'continue' | 'continue_with_conditions' | 'evaluate_alternatives' | 'initiate_offboarding' | 'immediate_action_required',
    executive_summary: '',
    next_review_date: '',
  });

  // Resolve the Trust Portal help screen id for this vendor (if any).
  // Returns null for vendors that don't have a Trust Portal entry, in
  // which case no help icon is rendered and the header looks unchanged.
  const helpScreenId = resolveVendorHelpScreenId(vendor.vendor_name);

  const handleDocumentUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    setIsUploading(true);
    try {
      await vendorAPI.uploadDocument(vendor.id, formData);
      setShowUploadModal(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to upload document:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateReview = async () => {
    // Don't allow starting a new review if one is already in progress
    const hasActiveReview = vendor.reviews?.some(
      r => r.review_status === 'pending' || r.review_status === 'in_progress'
    );
    if (hasActiveReview) {
      toast.error('A review is already in progress. Complete it before starting a new one.');
      return;
    }
    setIsStartingReview(true);
    try {
      await vendorAPI.createReview(vendor.id, { review_type: 'annual' });
      toast.success('Review started successfully.');
      onUpdate();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to start review.');
    } finally {
      setIsStartingReview(false);
    }
  };

  const openCompleteReviewModal = (reviewId: number) => {
    setCompletingReviewId(reviewId);
    const defaultNextDate = new Date();
    defaultNextDate.setFullYear(defaultNextDate.getFullYear() + 1);
    setCompleteReviewForm({
      recommendation: 'continue',
      executive_summary: '',
      next_review_date: defaultNextDate.toISOString().split('T')[0],
    });
    setShowCompleteReviewModal(true);
  };

  const handleCompleteReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!completingReviewId) return;
    setIsCompletingReview(true);
    try {
      await vendorAPI.completeReview(completingReviewId, {
        recommendation: completeReviewForm.recommendation,
        executive_summary: completeReviewForm.executive_summary || undefined,
        next_review_date: completeReviewForm.next_review_date || undefined,
      });
      setShowCompleteReviewModal(false);
      setCompletingReviewId(null);
      toast.success('Review completed successfully.');
      onUpdate();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to complete review.');
    } finally {
      setIsCompletingReview(false);
    }
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: boolean | string | null, type: 'boolean' | 'soc2' | 'dpa') => {
    if (type === 'boolean') {
      return status ? (
        <CheckCircle2 className="w-4 h-4 text-green-600" />
      ) : (
        <XCircle className="w-4 h-4 text-gray-400" />
      );
    }

    if (type === 'soc2') {
      switch (status) {
        case 'current': return <CheckCircle2 className="w-4 h-4 text-green-600" />;
        case 'expired': return <XCircle className="w-4 h-4 text-red-600" />;
        case 'pending': return <Clock className="w-4 h-4 text-yellow-600" />;
        default: return <span className="text-gray-400 text-xs">N/A</span>;
      }
    }

    if (type === 'dpa') {
      switch (status) {
        case 'signed': return <CheckCircle2 className="w-4 h-4 text-green-600" />;
        case 'pending': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
        case 'declined': return <XCircle className="w-4 h-4 text-red-600" />;
        default: return <span className="text-gray-400 text-xs">N/R</span>;
      }
    }

    return null;
  };

  const onboardingItems = [
    { key: 'security_questionnaire_sent', label: 'Security questionnaire sent' },
    { key: 'security_questionnaire_received', label: 'Security questionnaire received' },
    { key: 'soc2_reviewed', label: 'SOC 2 Type II reviewed' },
    { key: 'dpa_negotiated', label: 'DPA negotiated' },
    { key: 'dpa_signed', label: 'DPA signed' },
    { key: 'encryption_verified', label: 'Encryption capabilities verified' },
    { key: 'data_retention_documented', label: 'Data retention policy documented' },
    { key: 'incident_notification_confirmed', label: 'Incident notification requirements confirmed' },
    { key: 'right_to_audit_verified', label: 'Right-to-audit clause verified' },
    { key: 'insurance_confirmed', label: 'Insurance requirements confirmed' },
    { key: 'initial_risk_assessment', label: 'Initial risk assessment completed' },
    { key: 'annual_review_scheduled', label: 'Annual review scheduled' }
  ];

  const handleOnboardingToggle = async (key: string) => {
    const currentValue = vendor.onboarding_checklist[key] || false;
    try {
      await vendorAPI.updateOnboardingChecklist(vendor.id, { [key]: !currentValue });
      onUpdate();
    } catch (error) {
      console.error('Failed to update checklist:', error);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${getRiskColor(vendor.risk_level)}`}>
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{vendor.vendor_name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  vendor.tier === 1 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  Tier {vendor.tier} {vendor.tier === 1 ? '(Critical)' : '(Standard)'}
                </span>
                <span className="text-xs text-gray-500 capitalize">
                  {vendor.service_category.replace(/_/g, ' ')}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {helpScreenId && (
              <HelpIconButton screenId={helpScreenId} size="sm" />
            )}
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={() => setShowArchiveModal(true)}
              className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Archive className="w-4 h-4" />
              Archive
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="border-b border-gray-200">
          <nav className="flex gap-6 px-6">
            {[
              { id: 'overview' as DetailTab, label: 'Overview', icon: Settings },
              { id: 'documents' as DetailTab, label: 'Documents', icon: FileText, count: vendor.documents?.length },
              { id: 'reviews' as DetailTab, label: 'Reviews', icon: Clock, count: vendor.reviews?.length },
              { id: 'contacts' as DetailTab, label: 'Contacts', icon: Users, count: vendor.contacts?.length },
              { id: 'onboarding' as DetailTab, label: 'Onboarding', icon: CheckCircle2 }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== undefined && (
                  <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Security & Compliance
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">SOC 2 Status</span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(vendor.soc2_status, 'soc2')}
                        <span className="text-sm font-medium capitalize">{vendor.soc2_status}</span>
                      </div>
                    </div>
                    {vendor.soc2_expiration_date && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">SOC 2 Expires</span>
                        <span className="text-sm font-medium">{new Date(vendor.soc2_expiration_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">ISO 27001</span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(vendor.iso27001_certified, 'boolean')}
                        <span className="text-sm font-medium">{vendor.iso27001_certified ? 'Certified' : 'No'}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">DPA Status</span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(vendor.dpa_status, 'dpa')}
                        <span className="text-sm font-medium capitalize">{vendor.dpa_status}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Data Handling
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Data Access Level</span>
                      <span className="text-sm font-medium capitalize">{vendor.data_access_level.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Zero Data Retention</span>
                      {getStatusIcon(vendor.zero_data_retention, 'boolean')}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">AI Training Prohibited</span>
                      {getStatusIcon(vendor.ai_training_prohibited, 'boolean')}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Data Residency</span>
                      <span className="text-sm font-medium">{vendor.data_residency || 'Not specified'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Security Controls
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Encryption at Rest</span>
                      <span className="text-sm font-medium">{vendor.encryption_at_rest || 'Not specified'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Encryption in Transit</span>
                      <span className="text-sm font-medium">{vendor.encryption_in_transit || 'Not specified'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Breach Notification</span>
                      <span className="text-sm font-medium">{vendor.breach_notification_hours} hours</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Right to Audit</span>
                      {getStatusIcon(vendor.right_to_audit, 'boolean')}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Resources
                  </h3>
                  <div className="space-y-2">
                    {vendor.trust_center_url && (
                      <a
                        href={vendor.trust_center_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Trust Center
                      </a>
                    )}
                    {vendor.security_page_url && (
                      <a
                        href={vendor.security_page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Security Page
                      </a>
                    )}
                    {vendor.status_page_url && (
                      <a
                        href={vendor.status_page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Status Page
                      </a>
                    )}
                    {vendor.support_url && (
                      <a
                        href={vendor.support_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Support
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Risk Assessment
                </h3>
                <div className="flex items-center gap-6 mb-4">
                  <div className={`px-4 py-2 rounded-lg ${getRiskColor(vendor.risk_level)}`}>
                    <span className="text-2xl font-bold">{vendor.risk_score}</span>
                    <span className="text-sm ml-1 capitalize">({vendor.risk_level})</span>
                  </div>
                </div>
                {vendor.risk_score_factors && Object.keys(vendor.risk_score_factors).length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">Risk Score Breakdown:</h4>
                    {Object.entries(vendor.risk_score_factors).map(([key, factor]) => {
                      const isPositive = factor.value > 0;
                      return (
                        <div key={key} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{factor.reason}</span>
                          <span className={`font-medium ${isPositive ? 'text-red-600' : 'text-green-600'}`}>
                            {isPositive ? '+' : ''}{factor.value}
                          </span>
                        </div>
                      );
                    })}
                    <div className="pt-2 border-t mt-2 text-xs text-gray-500">
                      <span className="text-green-600">Green (negative)</span> = reduces risk, <span className="text-red-600">Red (positive)</span> = adds risk
                    </div>
                  </div>
                )}
              </div>

              {vendor.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Notes</h3>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{vendor.notes}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Vendor Documents</h3>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Upload className="w-4 h-4" />
                  Upload Document
                </button>
              </div>

              {vendor.documents && vendor.documents.length > 0 ? (
                <div className="space-y-3">
                  {vendor.documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-blue-600" />
                        <div>
                          <h4 className="font-medium text-gray-900">{doc.document_name}</h4>
                          <p className="text-sm text-gray-500 capitalize">{doc.document_type.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.expiration_date && (
                          <p className="text-sm text-gray-500">
                            Expires: {new Date(doc.expiration_date).toLocaleDateString()}
                          </p>
                        )}
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          doc.review_status === 'approved' ? 'bg-green-100 text-green-700' :
                          doc.review_status === 'expired' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {doc.review_status}
                        </span>
                        {doc.review_status === 'pending' && (
                          <button
                            onClick={async () => {
                              try {
                                await vendorAPI.updateDocumentStatus(doc.id, 'approved');
                                onUpdate();
                              } catch {
                                toast.error('Failed to approve document');
                              }
                            }}
                            className="text-xs px-2 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            Approve
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No documents uploaded yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Review History</h3>
                {!vendor.reviews?.some(r => r.review_status === 'pending' || r.review_status === 'in_progress') && (
                  <button
                    onClick={handleCreateReview}
                    disabled={isStartingReview}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                  >
                    {isStartingReview ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    {isStartingReview ? 'Starting...' : 'Start Review'}
                  </button>
                )}
              </div>

              {vendor.reviews && vendor.reviews.length > 0 ? (
                <div className="space-y-3">
                  {vendor.reviews.map(review => (
                    <div key={review.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 capitalize">{review.review_type} Review</h4>
                        <div className="flex items-center gap-2">
                          {(review.review_status === 'pending' || review.review_status === 'in_progress') && (
                            <button
                              onClick={() => openCompleteReviewModal(review.id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Complete Review
                            </button>
                          )}
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            review.review_status === 'completed' ? 'bg-green-100 text-green-700' :
                            review.review_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {review.review_status === 'in_progress' ? 'In Progress' :
                             review.review_status === 'completed' ? 'Completed' :
                             review.review_status === 'pending' ? 'Pending' :
                             review.review_status}
                          </span>
                        </div>
                      </div>
                      {review.executive_summary && (
                        <p className="text-sm text-gray-600 mb-2">{review.executive_summary}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        {review.review_completed_at && (
                          <span>Completed: {new Date(review.review_completed_at).toLocaleDateString()}</span>
                        )}
                        {review.recommendation && (
                          <span className="capitalize">Recommendation: {review.recommendation.replace(/_/g, ' ')}</span>
                        )}
                        {review.next_review_date && (
                          <span>Next Review: {new Date(review.next_review_date).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No reviews completed yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Vendor Contacts</h3>
                <button className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  <Plus className="w-4 h-4" />
                  Add Contact
                </button>
              </div>

              {vendor.contacts && vendor.contacts.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {vendor.contacts.map(contact => (
                    <div key={contact.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{contact.name}</h4>
                          <p className="text-xs text-gray-500 capitalize">{contact.contact_type}</p>
                        </div>
                        {contact.is_primary && (
                          <span className="ml-auto text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">Primary</span>
                        )}
                      </div>
                      {contact.title && <p className="text-sm text-gray-600 mb-1">{contact.title}</p>}
                      {contact.email && (
                        <a href={`mailto:${contact.email}`} className="text-sm text-blue-600 hover:underline block">
                          {contact.email}
                        </a>
                      )}
                      {contact.phone && <p className="text-sm text-gray-500">{contact.phone}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No contacts added yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'onboarding' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Onboarding Checklist</h3>
                  <p className="text-sm text-gray-500">
                    {Object.values(vendor.onboarding_checklist || {}).filter(Boolean).length} of {onboardingItems.length} completed
                  </p>
                </div>
                {vendor.onboarding_completed && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    Onboarding Complete
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {onboardingItems.map(item => {
                  const isChecked = vendor.onboarding_checklist?.[item.key] || false;
                  return (
                    <label
                      key={item.key}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        isChecked ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleOnboardingToggle(item.key)}
                        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className={`text-sm ${isChecked ? 'text-green-700' : 'text-gray-700'}`}>
                        {item.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

      {showArchiveModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Archive Vendor</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to archive {vendor.vendor_name}? This vendor will be removed from active management.
            </p>
            <textarea
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Reason for archiving (required)"
              className="w-full p-3 border border-gray-300 rounded-lg mb-4"
              rows={3}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (archiveReason.trim()) {
                    onArchive(archiveReason);
                    setShowArchiveModal(false);
                  }
                }}
                disabled={!archiveReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Archive Vendor
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showCompleteReviewModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Complete Review
            </h3>
            <form onSubmit={handleCompleteReview}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recommendation</label>
                  <select
                    value={completeReviewForm.recommendation}
                    onChange={(e) => setCompleteReviewForm({ ...completeReviewForm, recommendation: e.target.value as typeof completeReviewForm.recommendation })}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="continue">Continue — vendor meets requirements</option>
                    <option value="continue_with_conditions">Continue with Conditions</option>
                    <option value="evaluate_alternatives">Evaluate Alternatives</option>
                    <option value="initiate_offboarding">Initiate Offboarding</option>
                    <option value="immediate_action_required">Immediate Action Required</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Review Notes / Executive Summary</label>
                  <textarea
                    value={completeReviewForm.executive_summary}
                    onChange={(e) => setCompleteReviewForm({ ...completeReviewForm, executive_summary: e.target.value })}
                    placeholder="Summary of findings and any action items..."
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={4}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Next Review Date</label>
                  <input
                    type="date"
                    value={completeReviewForm.next_review_date}
                    onChange={(e) => setCompleteReviewForm({ ...completeReviewForm, next_review_date: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCompleteReviewModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCompletingReview}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isCompletingReview ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  {isCompletingReview ? 'Completing...' : 'Mark as Completed'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {showUploadModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Document</h3>
            <form onSubmit={handleDocumentUpload}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                  <select name="document_type" required className="w-full p-2 border border-gray-300 rounded-lg">
                    <option value="soc2_report">SOC 2 Report</option>
                    <option value="dpa">Data Processing Agreement</option>
                    <option value="iso27001_certificate">ISO 27001 Certificate</option>
                    <option value="security_questionnaire">Security Questionnaire</option>
                    <option value="msa">Master Service Agreement</option>
                    <option value="nda">NDA</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Name</label>
                  <input
                    type="text"
                    name="document_name"
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label>
                  <input
                    type="date"
                    name="expiration_date"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                  <input
                    type="file"
                    name="file"
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default VendorDetail;
